import "./LoginPage.css";

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api_without_cancellation } from "../api/api";
import PerfectCaseLogo from "../Components/Forms/PerfectCaseLogo";
import { getActivatedCoCounselCase } from "../auth/activationCase.mjs";
import { getFirmLoginPath } from "../auth/firmLogin.mjs";

const CreateSignupScreen = () => {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userNameSet, setUserNameSet] = useState("");
  const [errorMsgResetPass, setErrorMsgResetPass] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";
  const [sideBarCollapsed, setSideBarCollapsed] = useState(null);

  useEffect(() => {
    const getUserPreferencesFromStorage = () => {
      const preferences = localStorage.getItem("userPreferences");
      const preferencesObj = preferences ? JSON.parse(preferences) : null;
      setSideBarCollapsed(preferencesObj?.SidebarView);
    };
    getUserPreferencesFromStorage();
  }, []);

  async function handleCreateAccount(e) {
    e.preventDefault();
    if (creating) return;
    setErrorMsgResetPass("");

    if (!newPassword) {
      setErrorMsgResetPass("Password is empty");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsgResetPass("Passwords do not match");
      return;
    }

    setCreating(true);
    try {
      const response = await api_without_cancellation.post(
        "/api/treatment-opt/activate-account/",
        {
          uid: uid,
          token: token,
          username: userNameSet,
          password: newPassword,
        },
        { skipAuth: true }
      );

      if (response.status === 200 || response.status === 201) {
        const activatedCoCounselCase = getActivatedCoCounselCase(
          response.data?.activated_cocounsel_cases
        );
        navigate(getFirmLoginPath(response.data?.firm_login_slug), {
          state: { activatedCoCounselCase },
        });
      }
    } catch (error) {
      setErrorMsgResetPass(
        error.response?.data?.error || "Unable to activate the account. Please try again."
      );
    } finally {
      setCreating(false);
    }

    // Logic for resetting password
    // navigate("/login"); // After resetting password, navigate to login or any other desired page
  }

  const checkUsername = async (username) => {
    if (username.length < 3) return;

    try {
      const response = await fetch(
        `${origin}/accounts/check-username/?username=${encodeURIComponent(username)}`
      );

      const data = await response.json();

      if (!response.ok) {
        setUsernameAvailable(false);
        setUsernameError(data.message || "Error checking username");
      } else {
        if (data.available) {
          setUsernameAvailable(true);
          setUsernameError("");
        } else {
          setUsernameAvailable(false);
          setUsernameError("Username is already taken");
        }
      }
    } catch (err) {
      setUsernameAvailable(false);
      setUsernameError("Error checking username availability");
    }
  };

  useEffect(() => {
    if (userNameSet.length < 3) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    const timeoutId = setTimeout(() => {
      checkUsername(userNameSet);
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(timeoutId); // Clear previous timeout on cleanup
    };
  }, [userNameSet]);

  const showError =
    (userNameSet.length > 0 && userNameSet.length < 3) ||
    usernameError ||
    usernameAvailable === true;

  return (
    <div className="login-page">
      <div className={`page-wrapper ${sideBarCollapsed ? "force-collapsed" : ""}`}>
        <div className="page-container">
          <div className="main-content">
            <div
              className="section__content section__content--p30 login-form"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-12 top-head">
                    <form onSubmit={handleCreateAccount}>
                      <div className="container">
                        <PerfectCaseLogo />
                        {errorMsgResetPass && (
                          <div className="alert alert-danger" role="alert">
                            {errorMsgResetPass}
                          </div>
                        )}
                        <span className="username-set">
                          <i className="fa fa-user" aria-hidden="true" />
                          <input
                            value={userNameSet}
                            onChange={(e) => setUserNameSet(e.target.value)}
                            type="text"
                            placeholder="Username"
                            name="username"
                            id="username"
                            required
                            style={{ marginBottom: showError ? 0 : "22px" }}
                          />
                        </span>
                        {userNameSet.length > 0 && userNameSet.length < 3 && (
                          <div
                            className="invalid-feedback"
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              textAlign: "left",
                              display: "block",
                              marginTop: "0px",
                            }}
                          >
                            Username must be at least 3 characters
                          </div>
                        )}
                        {usernameError && (
                          <div
                            className="invalid-feedback"
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              textAlign: "left",
                              display: "block",
                              marginTop: "0px",
                            }}
                          >
                            {usernameError}
                          </div>
                        )}
                        {usernameAvailable === true && (
                          <div
                            className="valid-feedback"
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              textAlign: "left",
                              display: "block",
                              marginTop: "0px",
                            }}
                          >
                            Username is available
                          </div>
                        )}
                        <span className="username-set">
                          <i
                            className="fa fa-lock"
                            aria-hidden="true"
                            style={{
                              left: showError ? "21px" : "-429px",
                              top: showError ? "4px" : "82px",
                            }}
                          />
                          <input
                            type="password"
                            placeholder="New Password"
                            name="new_password"
                            id="new_password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                          />
                        </span>
                        <span className="username-set">
                          <i
                            className="fa fa-lock"
                            aria-hidden="true"
                            style={{
                              left: "-429px",
                              top: "82px",
                            }}
                          />
                          <input
                            type="password"
                            placeholder="Confirm Password"
                            name="confirm_password"
                            id="confirm_password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                          />
                        </span>
                        <button className="cancelbtn" type="submit" disabled={creating}>
                          Create Account
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSignupScreen;
